import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  AlertTriangle, ArrowLeft, ArrowRight, Ban, Phone, PhoneOff, Clock, ChevronLeft,
  IndianRupee, QrCode, Link2, CheckCircle2, XCircle, HandCoins,
} from 'lucide-react'

import { Link, useNavigate, useParams } from '@/lib/router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { date, dateTime, dpd, inr, inrFull, parseAmount } from '@/lib/format'
import { cn } from '@/lib/utils'

import {
  NOTE_REQUIRED_OUTCOMES,
  useActivity, useCase, useCaptureDisposition, useConductStatus,
  type ContactResult, type DispositionRequest, type Outcome,
} from '@/features/cases/api'
import { DL, DLRow, DuesBreakupCard } from '@/features/cases/CaseDetailBody'
import {
  useCallState, useCasePayments, useContactNumbers, useDial, useHangup,
  useRaisePayment, useSimulatePayment, useTeleQueue,
  type CallState, type Mood, type PaymentChannel,
} from './api'
import { LANGUAGES, languageLabel } from './languages'

/**
 * Canonical contact results, in the order a telecaller meets them. Labels are
 * the floor's words; the values are the canonical taxonomy the server stores.
 */
const CONTACT_RESULTS: { value: ContactResult; label: string }[] = [
  { value: 'CONNECTED', label: 'Connected' },
  { value: 'NO_ANSWER', label: 'No answer' },
  { value: 'BUSY', label: 'Busy' },
  { value: 'SWITCHED_OFF', label: 'Switched off' },
  { value: 'CALLBACK_REQUESTED', label: 'Call back later' },
  { value: 'WRONG_NUMBER', label: 'Wrong number' },
  { value: 'INVALID_NUMBER', label: 'Invalid number' },
  { value: 'CUSTOMER_DISCONNECTED', label: 'Disconnected (CDC)' },
  { value: 'PERMANENT_NON_CONTACTABLE', label: 'Non-contactable (PNC)' },
]

const OUTCOMES: { value: Outcome; label: string }[] = [
  { value: 'PROMISE_TO_PAY', label: 'Promise to pay' },
  { value: 'PARTIAL_PROMISE', label: 'Partial promise' },
  { value: 'ALREADY_PAID', label: 'Already paid' },
  { value: 'NEGOTIATION', label: 'Negotiating' },
  { value: 'DISPUTE', label: 'Dispute' },
  { value: 'REFUSAL', label: 'Refusal' },
  { value: 'THIRD_PARTY_RESPONSE', label: 'Third party' },
  { value: 'NOT_REACHABLE', label: 'Not reachable' },
  { value: 'NO_RESPONSE_NEEDED', label: 'No action needed' },
  { value: 'DECEASED_REPORTED', label: 'Death reported' },
  { value: 'CLOSURE_REQUEST', label: 'Foreclosure request (FC)' },
  { value: 'SETTLEMENT_REQUEST', label: 'Settlement / waiver (RSW)' },
  { value: 'BROKEN_PTP', label: 'Broken PTP' },
]

/**
 * How a finished payment request reads on the floor.
 *
 * The old list right-aligned the raw status word in grey and gave a failure
 * the same weight as a success, so "what happened to the link I sent?" — the
 * one question this list exists to answer — took a squint. Failure is the
 * state an agent has to act on, so it is the loudest.
 *
 * Labels are outcomes, not gateway vocabulary: a borrower's money either
 * arrived or it did not.
 */
interface PaymentOutcome {
  label: string; icon: typeof CheckCircle2; row: string; pill: string; fg: string
}

/** Fallback for a status this build has not met — shown, never swallowed. */
const CANCELLED_OUTCOME: PaymentOutcome = {
  label: 'Cancelled', icon: Ban,
  row: 'border-slate-200 bg-slate-50',
  pill: 'bg-slate-100 text-navy-600', fg: 'text-muted-foreground',
}

const PAYMENT_OUTCOMES: Record<string, PaymentOutcome> = {
  SUCCESS: {
    label: 'Paid', icon: CheckCircle2,
    row: 'border-positive/30 bg-positive-soft/50',
    pill: 'bg-positive-soft text-positive', fg: 'text-positive',
  },
  FAILED: {
    label: 'Failed', icon: XCircle,
    row: 'border-negative/30 bg-negative-soft/40',
    pill: 'bg-negative-soft text-negative', fg: 'text-negative',
  },
  EXPIRED: {
    label: 'Expired', icon: Clock,
    row: 'border-slate-200 bg-slate-50',
    pill: 'bg-slate-100 text-navy-600', fg: 'text-muted-foreground',
  },
  CANCELLED: CANCELLED_OUTCOME,
}

/** "SIMULATED_FAILURE" → "Simulated failure". Gateway codes, read aloud. */
function humanReason(code: string): string {
  const words = code.replace(/[_-]+/g, ' ').trim().toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Sentinel for the language select's free-text mode. Never sent as a value. */
const OTHER_LANGUAGE = '__other__'

const MOODS: { value: Mood; label: string; tone: string }[] = [
  { value: 'NORMAL', label: 'Normal', tone: 'border-positive/40 bg-positive-soft text-positive' },
  { value: 'ANGER', label: 'Anger', tone: 'border-warning/40 bg-warning-soft text-warning' },
  { value: 'IRATE', label: 'Irate', tone: 'border-negative/40 bg-negative-soft text-negative' },
]

const PROMISE_OUTCOMES: Outcome[] = ['PROMISE_TO_PAY', 'PARTIAL_PROMISE']

export function CallConsolePage() {
  const { caseId } = useParams<{ caseId: string }>({ from: '/tele/console/$caseId' })
  const navigate = useNavigate()

  const { data: queue } = useTeleQueue()
  const row = queue?.find((r) => r.caseId === caseId)

  // Step in queue order, skipping conduct-blocked cases in both directions —
  // stepping onto a case you cannot dial is a dead end whichever way you came.
  // Position is reported so the agent knows where they are in the shift.
  const { prevCase, nextCase, position } = useMemo(() => {
    if (!queue) return { prevCase: undefined, nextCase: undefined, position: null }
    const i = queue.findIndex((r) => r.caseId === caseId)
    if (i < 0) return { prevCase: undefined, nextCase: undefined, position: null }
    return {
      prevCase: queue.slice(0, i).reverse().find((r) => r.conductBlocks.length === 0),
      nextCase: queue.slice(i + 1).find((r) => r.conductBlocks.length === 0),
      position: `${i + 1} of ${queue.length}`,
    }
  }, [queue, caseId])

  const { data: conduct } = useConductStatus(caseId)
  const blocks = conduct?.blocks ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to="/tele/queue" className="inline-flex items-center gap-1 text-2xs uppercase tracking-wider text-navy-600 hover:text-brand">
            <ChevronLeft className="h-3 w-3" />
            My queue
          </Link>
          <h1 className="truncate text-xl font-semibold tracking-tight text-navy-900">
            {row?.customerName ?? 'Case'}
          </h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono text-xs">{row?.loanAccountNo}</span>
            {row && <span>· {dpd(row.currentDpd)} DPD</span>}
            {/* The demand, not POS. This is the number the agent says out
                loud, so it is the one in the header. */}
            {row && (
              <span className="font-medium text-navy-900">
                · {row.currentOverdue ? inr(row.currentOverdue) : '—'} due
              </span>
            )}
            {row?.preferredLanguage && (
              <Badge>{languageLabel(row.preferredLanguage)}</Badge>
            )}
            {/* Straight off the queue row — no extra fetch, and it is the
                first thing a customer argues about on a follow-up call. */}
            {row?.ptpAmount && (
              <span className="inline-flex items-center gap-1 rounded border border-positive/30 bg-positive-soft px-1.5 py-0.5 text-2xs font-medium text-positive">
                <HandCoins className="h-3 w-3" />
                Promised {inr(row.ptpAmount)}{row.ptpDueOn ? ` for ${date(row.ptpDueOn)}` : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {position && (
            <span className="hidden text-2xs uppercase tracking-wider text-navy-600 sm:inline">
              {position}
            </span>
          )}
          <Button
            variant="outline"
            disabled={!prevCase}
            onClick={() => prevCase && navigate({ to: '/tele/console/$caseId', params: { caseId: prevCase.caseId } })}
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Previous
          </Button>
          <Button
            variant="outline"
            disabled={!nextCase}
            onClick={() => nextCase && navigate({ to: '/tele/console/$caseId', params: { caseId: nextCase.caseId } })}
          >
            Next case
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {blocks.length > 0 && (
        <div className="rounded-md border border-warning/30 bg-warning-soft p-3">
          <p className="flex items-center gap-1.5 text-sm font-medium text-warning">
            <AlertTriangle className="h-4 w-4" />
            Conduct block
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-navy-800">
            {blocks.map((b) => <li key={b.code}>{b.message}</li>)}
          </ul>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Keyed by case: the form holds a language, a mood and free-text
            notes about the person on the line, and Next case must not carry
            any of it onto the next customer. */}
        <ActionPane key={caseId} caseId={caseId} blocked={blocks.length > 0}
                    due={row?.currentOverdue ?? null} />
        <HistoryPane caseId={caseId} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Left: dial + capture, or raise a payment request
// ---------------------------------------------------------------------------

function ActionPane({ caseId, blocked, due }: {
  caseId: string; blocked: boolean; due: string | null
}) {
  const [tab, setTab] = useState<'disposition' | 'payment'>('disposition')
  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {([['disposition', 'Add disposition'], ['payment', 'Send payment request']] as const).map(
          ([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm transition-colors',
                tab === key
                  ? 'bg-brand-soft font-medium text-navy-900 shadow-soft'
                  : 'text-navy-700 hover:bg-navy-50',
              )}
            >
              {label}
            </button>
          ),
        )}
      </div>
      {tab === 'disposition'
        ? <CapturePane caseId={caseId} blocked={blocked} />
        : <PaymentPane caseId={caseId} due={due} />}
    </div>
  )
}

function CapturePane({ caseId, blocked }: { caseId: string; blocked: boolean }) {
  const { data: numbers } = useContactNumbers(caseId)
  const dial = useDial()
  const hangup = useHangup()
  const capture = useCaptureDisposition(caseId)

  const [numberRef, setNumberRef] = useState<string | null>(null)
  const [callId, setCallId] = useState<string | null>(null)
  const { data: call } = useCallState(callId)

  const [contactResult, setContactResult] = useState<ContactResult | null>(null)
  const [outcome, setOutcome] = useState<Outcome | ''>('')
  const [mood, setMood] = useState<Mood | ''>('')
  const [language, setLanguage] = useState('')
  // "Other" is a mode, not a value: the select shows it while the text box
  // holds whatever the agent types, so an unlisted language still reaches the
  // server as itself rather than as the string "Other".
  const [languageOther, setLanguageOther] = useState(false)
  const [notes, setNotes] = useState('')
  const [callbackAt, setCallbackAt] = useState('')
  const [promisedAmount, setPromisedAmount] = useState('')
  const [promisedDate, setPromisedDate] = useState(defaultPromiseDate())

  // Default to the first number we hold, so the common case is one click.
  useEffect(() => {
    if (!numberRef && numbers && numbers.length > 0) setNumberRef(numbers[0]!.ref)
  }, [numbers, numberRef])

  // The dialer already knows how the call ended. Pre-select that rather than
  // making the agent retype it — but only as a default they can override,
  // because a provider cannot tell a wrong number from a right one that hung up.
  useEffect(() => {
    if (call?.suggestedContactResult && !contactResult) {
      setContactResult(call.suggestedContactResult as ContactResult)
    }
  }, [call?.suggestedContactResult, contactResult])

  const promise = PROMISE_OUTCOMES.includes(outcome as Outcome)
  const needsOutcome = contactResult === 'CONNECTED'
  const needsCallback = contactResult === 'CALLBACK_REQUESTED'
  // Normalised here rather than at the input, so the agent keeps seeing the
  // grouping they typed while the server gets a number it can bind.
  const parsedPromise = parseAmount(promisedAmount)
  const canSubmit =
    !!contactResult &&
    (!needsOutcome || !!outcome) &&
    (!needsCallback || !!callbackAt) &&
    (!promise || !!parsedPromise) &&
    (!NOTE_REQUIRED_OUTCOMES.has(outcome) || !!notes.trim())

  function reset() {
    setContactResult(null); setOutcome(''); setMood(''); setNotes('')
    setCallbackAt(''); setPromisedAmount(''); setPromisedDate(defaultPromiseDate())
    setLanguage(''); setLanguageOther(false)
    setCallId(null)
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!contactResult) return
    const body: DispositionRequest = {
      // No channel — derived server-side from the allocation slot.
      contactResult,
      outcome: outcome || null,
      notes: notes.trim() || null,
      ptp: promise && parsedPromise
        ? { promisedAmount: parsedPromise, promisedDate, graceDays: 1, notes: notes.trim() || undefined }
        : null,
      call: {
        dialledNumberRef: numberRef,
        callbackAt: callbackAt ? new Date(callbackAt).toISOString() : null,
        mood: mood || null,
        preferredLanguage: language.trim() || null,
        providerCallId: call?.providerCallId ?? null,
        callStartedAt: call?.startedAt ?? null,
        callEndedAt: call?.endedAt ?? null,
        recordingUrl: call?.recordingUrl ?? null,
      },
    }
    capture.mutate(body, { onSuccess: reset })
  }

  const dialError = dial.isError && isAxiosError(dial.error)
    ? (dial.error.response?.data as { code?: string; message?: string } | undefined)
    : undefined

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add disposition</CardTitle>
        <CardDescription>
          Dial by number, then record what happened. The number never leaves the server.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Dial strip */}
        <div className="rounded-md border border-slate-200 bg-navy-50/40 p-3">
          <Label>Number</Label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(numbers ?? []).map((n) => (
              <button
                key={n.ref}
                type="button"
                onClick={() => setNumberRef(n.ref)}
                className={cn(
                  'rounded border px-2 py-1 text-left text-xs transition-colors',
                  numberRef === n.ref
                    ? 'border-brand bg-brand-soft text-navy-900'
                    : 'border-slate-200 bg-white text-navy-700 hover:border-navy-200',
                )}
              >
                <span className="block font-medium">{n.label}</span>
                <span className="block font-mono text-2xs text-muted-foreground">{n.masked}</span>
              </button>
            ))}
            {numbers && numbers.length === 0 && (
              <p className="text-xs text-muted-foreground">No number on file for this customer.</p>
            )}
          </div>

          <div className="mt-2.5 flex items-center gap-2">
            <Button
              size="sm"
              disabled={!numberRef || blocked || dial.isPending || (!!call && !call.terminal)}
              onClick={() => numberRef && dial.mutate(
                { caseId, dialledNumberRef: numberRef },
                { onSuccess: (d) => setCallId(d.providerCallId) },
              )}
            >
              <Phone className="mr-1.5 h-3.5 w-3.5" />
              {dial.isPending ? 'Dialling…' : 'Call'}
            </Button>
            {call && !call.terminal && (
              <Button size="sm" variant="outline" onClick={() => callId && hangup.mutate(callId)}>
                <PhoneOff className="mr-1.5 h-3.5 w-3.5" />
                Hang up
              </Button>
            )}
            {blocked && (
              <span className="flex items-center gap-1 text-2xs text-warning">
                <Ban className="h-3 w-3" /> Blocked by conduct policy
              </span>
            )}
            {call && <CallPill call={call} />}
          </div>

          {dialError && (
            <p className="mt-2 text-xs text-warning">
              {dialError.message}
              {dialError.code?.startsWith('telephony_') && ' You can still capture the call manually below.'}
            </p>
          )}
        </div>

        {/* Capture */}
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Call status</Label>
            <div className="mt-1 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {CONTACT_RESULTS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setContactResult(c.value)}
                  className={cn(
                    'rounded border px-2 py-1.5 text-xs transition-colors',
                    contactResult === c.value
                      ? 'border-brand bg-brand-soft font-medium text-navy-900'
                      : 'border-slate-200 bg-white text-navy-700 hover:border-navy-200',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {needsOutcome && (
            <div>
              <Label htmlFor="outcome">Outcome</Label>
              <select
                id="outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as Outcome)}
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-sm"
              >
                <option value="">Select an outcome…</option>
                {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          {promise && (
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label htmlFor="amt">Promised amount</Label>
                <Input id="amt" inputMode="decimal" value={promisedAmount}
                       onChange={(e) => setPromisedAmount(e.target.value)} placeholder="5,000" />
                {promisedAmount && !parsedPromise && (
                  <p className="mt-1 text-2xs text-warning">Enter an amount, like 5,000 or 5000.50</p>
                )}
              </div>
              <div>
                <Label htmlFor="pdate">Promised date</Label>
                <Input id="pdate" type="date" value={promisedDate}
                       onChange={(e) => setPromisedDate(e.target.value)} />
              </div>
            </div>
          )}

          {needsCallback && (
            <div>
              <Label htmlFor="cb">Follow-up date and time</Label>
              <Input id="cb" type="datetime-local" value={callbackAt}
                     onChange={(e) => setCallbackAt(e.target.value)} />
              <p className="mt-1 text-2xs text-muted-foreground">
                Required. A callback with no time is the thing this field exists to stop losing.
              </p>
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label>Mood</Label>
              <div className="mt-1 flex gap-1.5">
                {MOODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMood(mood === m.value ? '' : m.value)}
                    className={cn(
                      'rounded border px-2 py-1 text-xs transition-colors',
                      mood === m.value ? m.tone : 'border-slate-200 bg-white text-navy-700',
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="lang">Language</Label>
              <select
                id="lang"
                value={languageOther ? OTHER_LANGUAGE : language}
                onChange={(e) => {
                  const v = e.target.value
                  setLanguageOther(v === OTHER_LANGUAGE)
                  setLanguage(v === OTHER_LANGUAGE ? '' : v)
                }}
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-sm"
              >
                <option value="">Unchanged</option>
                {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                <option value={OTHER_LANGUAGE}>Other…</option>
              </select>
              {languageOther && (
                <Input
                  className="mt-1.5"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="Language the customer speaks"
                  aria-label="Other language"
                />
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="notes">
              {NOTE_REQUIRED_OUTCOMES.has(outcome) ? 'What the customer said (required)' : 'Additional comment'}
            </Label>
            <Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {capture.isError && (
            <p className="text-xs text-negative">
              {isAxiosError(capture.error)
                ? (capture.error.response?.data as { message?: string })?.message ?? 'Could not save'
                : 'Could not save'}
            </p>
          )}

          <Button type="submit" disabled={!canSubmit || capture.isPending}>
            {capture.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Raise a UPI link or QR while the customer is still on the line.
 *
 * <p>Deliberately the same endpoints the mobile collection journey uses. A
 * telecaller raising a link mid-call and a field agent raising a QR at the
 * doorstep are the same act, and giving telecalling its own payment path is
 * how two ledgers of the same money start disagreeing.</p>
 */
function PaymentPane({ caseId, due }: { caseId: string; due: string | null }) {
  const { data: payments } = useCasePayments(caseId)
  // For the EMI-only quick-fill. Same query the Loan details tab reads, so on
  // a case the agent has already looked at this costs nothing.
  const { data: detail } = useCase(caseId)
  const raise = useRaisePayment(caseId)
  const simulate = useSimulatePayment(caseId)

  const [amount, setAmount] = useState('')
  const [channel, setChannel] = useState<PaymentChannel>('UPI_LINK')

  const parsedAmount = parseAmount(amount)

  // The instalment part of the arrears — overdue principal + interest, no
  // charges. Borrowers argue the charges and agree the EMI, so an agent
  // settling for the instalment alone needs it in one click rather than doing
  // the subtraction in their head mid-call.
  //
  // Not head.emiAmount, which is one contractual instalment: this is however
  // many instalments have gone unpaid.
  const emiDue = detail?.dues
    ? (Number(detail.dues.principalDue) + Number(detail.dues.interestDue)).toFixed(2)
    : null

  const live = payments?.find((p) => p.status === 'CREATED' || p.status === 'PENDING')
  const settled = payments?.filter((p) => p.status !== 'CREATED' && p.status !== 'PENDING') ?? []

  const raiseError = raise.isError && isAxiosError(raise.error)
    ? (raise.error.response?.data as { message?: string } | undefined)?.message
    : undefined

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send payment request</CardTitle>
        <CardDescription>
          A UPI link or QR, raised through the same gateway the field app uses.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {live ? (
          <div className="rounded-md border border-brand/30 bg-brand-soft p-3">
            <p className="text-sm font-medium text-navy-900">
              {inr(live.amount)} · {live.channel === 'UPI_QR' ? 'QR' : 'Link'} awaiting payment
            </p>
            {live.payLink && (
              <p className="mt-1 break-all font-mono text-2xs text-navy-700">{live.payLink}</p>
            )}
            {live.qrPayload && (
              <p className="mt-1 break-all font-mono text-2xs text-navy-700">{live.qrPayload}</p>
            )}
            <p className="mt-1 text-2xs text-muted-foreground">
              Polling for the gateway to confirm — the customer's device saying so is not enough.
            </p>
            {/* Mock mode only; the server 404s this for a LIVE tenant. */}
            {live.gateway === 'MOCK' && (
              <div className="mt-2 flex gap-1.5">
                <Button size="sm" variant="outline" disabled={simulate.isPending}
                        onClick={() => simulate.mutate({ paymentId: live.paymentId, success: true })}>
                  Simulate paid
                </Button>
                <Button size="sm" variant="ghost" disabled={simulate.isPending}
                        onClick={() => simulate.mutate({ paymentId: live.paymentId, success: false })}>
                  Simulate failure
                </Button>
              </div>
            )}
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (parsedAmount) {
                raise.mutate({ amount: parsedAmount, channel }, { onSuccess: () => setAmount('') })
              }
            }}
          >
            <div>
              <Label htmlFor="amount">Amount</Label>
              <Input id="amount" inputMode="decimal" value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     placeholder="Amount to request" />
              {amount && !parsedAmount && (
                <p className="mt-1 text-2xs text-warning">Enter an amount, like 5,000 or 5000.50</p>
              )}
              {/* The demand, not POS. The one-click amounts have to be figures
                  the agent could have quoted on the call; offering the whole
                  outstanding principal here invites asking a borrower for a
                  figure nobody demanded. */}
              {(due || (emiDue && Number(emiDue) > 0)) && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {due && (
                    <button type="button" onClick={() => setAmount(String(due))}
                            className="text-2xs text-brand hover:underline">
                      Use full amount due ({inr(due)})
                    </button>
                  )}
                  {emiDue && Number(emiDue) > 0 && (
                    <button type="button" onClick={() => setAmount(emiDue)}
                            className="text-2xs text-brand hover:underline">
                      Use EMI due ({inr(emiDue)})
                    </button>
                  )}
                </div>
              )}
              {emiDue && Number(emiDue) > 0 && (
                <p className="mt-0.5 text-2xs text-muted-foreground">
                  EMI due is overdue principal + interest. The full amount adds
                  bounce, late-payment and other charges.
                </p>
              )}
            </div>
            <div>
              <Label>Method</Label>
              <div className="mt-1 flex gap-1.5">
                {([['UPI_LINK', 'Payment link', Link2], ['UPI_QR', 'Dynamic QR', QrCode]] as const)
                  .map(([value, label, Icon]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setChannel(value)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs transition-colors',
                        channel === value
                          ? 'border-brand bg-brand-soft font-medium text-navy-900'
                          : 'border-slate-200 bg-white text-navy-700 hover:border-navy-200',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
              </div>
            </div>
            {raiseError && <p className="text-xs text-warning">{raiseError}</p>}
            <Button type="submit" disabled={!parsedAmount || raise.isPending}>
              <IndianRupee className="mr-1.5 h-3.5 w-3.5" />
              {raise.isPending ? 'Raising…' : 'Raise request'}
            </Button>
          </form>
        )}

        {settled.length > 0 && (
          <div className="border-t border-slate-100 pt-2">
            <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
              Earlier requests
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {settled.map((p) => {
                const t = PAYMENT_OUTCOMES[p.status] ?? CANCELLED_OUTCOME
                const Icon = t.icon
                return (
                  <li key={p.paymentId} className={cn('rounded-md border px-2.5 py-1.5', t.row)}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon className={cn('h-3.5 w-3.5 shrink-0', t.fg)} />
                        <span className="text-sm font-medium tabular-nums text-navy-900">
                          {inrFull(p.amount)}
                        </span>
                      </span>
                      <span className={cn(
                        'shrink-0 rounded px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider',
                        t.pill,
                      )}>
                        {t.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-2xs text-muted-foreground">
                      {p.channel === 'UPI_QR' ? 'Dynamic QR' : 'Payment link'}
                      {' · '}
                      {p.status === 'SUCCESS' && p.paidAt
                        ? `paid ${dateTime(p.paidAt)}`
                        : `raised ${dateTime(p.initiatedAt)}`}
                    </p>
                    {/* Why it ended that way. A failed request with no reason
                        is the thing an agent gets asked about and cannot
                        answer, and a success with no UTR cannot be matched to
                        the bank statement. */}
                    {p.status === 'SUCCESS' && p.utr && (
                      <p className="mt-0.5 font-mono text-2xs text-navy-700">UTR {p.utr}</p>
                    )}
                    {p.status !== 'SUCCESS' && p.failureReason && (
                      <p className={cn('mt-0.5 text-2xs', t.fg)}>{humanReason(p.failureReason)}</p>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function CallPill({ call }: { call: CallState }) {
  const tone = call.status === 'COMPLETED' || call.status === 'CONNECTED'
    ? 'border-positive/40 bg-positive-soft text-positive'
    : call.terminal ? 'border-warning/40 bg-warning-soft text-warning'
    : 'border-brand/40 bg-brand-soft text-brand'
  return (
    <span className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-2xs font-medium', tone)}>
      {!call.terminal && <Clock className="h-3 w-3 animate-pulse" />}
      {call.status.replace(/_/g, ' ').toLowerCase()}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Right: history
// ---------------------------------------------------------------------------

function HistoryPane({ caseId }: { caseId: string }) {
  const [tab, setTab] = useState<'dispositions' | 'calls' | 'loan'>('dispositions')
  const { data: activity } = useActivity(caseId)
  // Only fetched when the agent opens the tab: GET /v1/cases/{id} discloses
  // the decrypted mobile and address and writes a PII access row, so it must
  // not fire on every console load.
  const { data: detail } = useCase(tab === 'loan' ? caseId : null)

  const dispositions = (activity ?? []).filter((a) => a.kind === 'DISPOSITION')
  // The Calls tab is the subset that came through a dialer — a row with a
  // recording or a call window. A hand-typed capture is a disposition but not
  // a call, and conflating them would inflate every per-call metric.
  const calls = dispositions.filter((a) => !!a.call?.providerCallId)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>History</CardTitle>
        <div className="mt-2 flex gap-1">
          {([['dispositions', `Dispositions (${dispositions.length})`],
             ['calls', `Calls (${calls.length})`],
             ['loan', 'Loan details']] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                'rounded px-2.5 py-1 text-xs transition-colors',
                tab === key ? 'bg-brand-soft font-medium text-navy-900' : 'text-navy-700 hover:bg-navy-50',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {tab === 'dispositions' && (
          dispositions.length === 0
            ? <p className="p-8 text-center text-sm text-muted-foreground">No history on this case yet.</p>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-2xs uppercase tracking-wider text-navy-600">
                    <th className="px-4 py-2 text-left font-semibold">Date</th>
                    <th className="px-4 py-2 text-left font-semibold">Mood</th>
                    <th className="px-4 py-2 text-left font-semibold">Disposition</th>
                    <th className="px-4 py-2 text-left font-semibold">By</th>
                  </tr>
                </thead>
                <tbody>
                  {dispositions.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2 text-xs text-muted-foreground">{dateTime(a.at)}</td>
                      <td className="px-4 py-2"><MoodBadge mood={a.call?.mood ?? null} /></td>
                      <td className="px-4 py-2">
                        <span className="text-navy-900">
                          {a.disposition?.contactResult.replace(/_/g, ' ').toLowerCase()}
                        </span>
                        {a.disposition?.outcome && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            · {a.disposition.outcome.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        )}
                        {a.call?.callbackAt && (
                          <span className="mt-0.5 flex items-center gap-1 text-2xs text-brand">
                            <Clock className="h-3 w-3" /> Callback {dateTime(a.call.callbackAt)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-2xs text-navy-700">{a.actorEmpId ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        )}

        {tab === 'loan' && (
          <div className="space-y-3 p-4">
            {!detail && <p className="text-sm text-muted-foreground">Loading…</p>}
            {detail && (
              <>
                <DL>
                  <DLRow label="Customer" value={detail.head.customerName} />
                  <DLRow label="Loan account" value={<span className="font-mono text-xs">{detail.head.loanAccountNo}</span>} />
                  <DLRow label="Product" value={detail.head.productType ?? '—'} />
                  <DLRow label="EMI" value={<span className="tabular-nums">{inrFull(detail.head.emiAmount)}</span>} />
                  <DLRow label="Sanctioned" value={<span className="tabular-nums">{inrFull(detail.head.sanctionAmount)}</span>} />
                  <DLRow label="Tenure" value={detail.head.tenureMonths != null ? `${detail.head.tenureMonths} months` : '—'} />
                  <DLRow label="Interest rate" value={detail.head.interestRate != null ? `${Number(detail.head.interestRate).toFixed(2)}%` : '—'} />
                  <DLRow label="Disbursed on" value={date(detail.head.disbursalDate)} />
                  <DLRow label="DPD" value={<span className="tabular-nums">{dpd(detail.head.currentDpd)}</span>} />
                  <DLRow label="Bucket" value={detail.head.currentBucket ?? '—'} />
                  {/* Portfolio context, explicitly labelled so it is never
                      mistaken for the ask. */}
                  <DLRow
                    label="Principal outstanding (POS)"
                    value={<span className="tabular-nums text-muted-foreground">{inrFull(detail.head.currentOutstanding)}</span>}
                  />
                </DL>
                {/* The same card the case-detail screen uses. A telecaller
                    quoting a different payoff from the one ops sees is exactly
                    what a second copy of this would produce. */}
                <DuesBreakupCard dues={detail.dues} />
              </>
            )}
          </div>
        )}

        {tab === 'calls' && (
          <div>
            {calls.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground">
                No dialled calls on this case yet.
              </p>
            )}
            <ul className="divide-y divide-slate-100">
              {calls.map((a) => (
                <li key={a.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-navy-900">
                        {a.disposition?.contactResult.replace(/_/g, ' ').toLowerCase()}
                        <span className="ml-1.5 font-mono text-2xs text-muted-foreground">
                          {a.call?.dialledNumberRef?.replace(/_/g, ' ').toLowerCase()}
                        </span>
                      </p>
                      <p className="text-2xs text-muted-foreground">
                        {dateTime(a.at)}
                        {callDuration(a.call?.callStartedAt, a.call?.callEndedAt) && (
                          <> · {callDuration(a.call?.callStartedAt, a.call?.callEndedAt)}</>
                        )}
                      </p>
                    </div>
                    <MoodBadge mood={a.call?.mood ?? null} />
                  </div>
                  {a.call?.recordingUrl && (
                    /* Recordings are on an .invalid host under the mock
                       provider, so the element renders but will not load —
                       that is correct: there is no audio to play until a real
                       vendor is wired. */
                    <audio
                      controls
                      preload="none"
                      src={a.call.recordingUrl}
                      className="mt-2 h-8 w-full"
                    >
                      <track kind="captions" />
                    </audio>
                  )}
                </li>
              ))}
            </ul>
            <p className="border-t border-slate-100 p-4 text-2xs text-muted-foreground">
              Per-channel history from before the channel derivation landed is unreliable — the
              channel used to be whatever the client claimed. Rows captured from here on are accurate.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function MoodBadge({ mood }: { mood: string | null }) {
  if (!mood) return <span className="text-2xs text-muted-foreground">—</span>
  const m = MOODS.find((x) => x.value === mood)
  return (
    <span className={cn(
      'inline-flex rounded border px-1.5 py-0.5 text-2xs font-medium',
      m?.tone ?? 'border-slate-200 bg-white text-navy-700',
    )}>
      {m?.label ?? mood}
    </span>
  )
}

function callDuration(startedAt?: string | null, endedAt?: string | null): string | null {
  if (!startedAt || !endedAt) return null
  const secs = Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000)
  if (secs < 0) return null
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function defaultPromiseDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 3)
  return d.toISOString().slice(0, 10)
}
